/**
 * Copyright 2026 Bluecircle Technologies Pvt Limited. All rights reserved.
 *
 * This software and its source code are the proprietary and confidential
 * intellectual property of Bluecircle Technologies Pvt Limited. Unauthorized
 * copying, reproduction, distribution, modification, public display, or use
 * of this software, in whole or in part, is strictly prohibited without the
 * prior written permission of Bluecircle Technologies Pvt Limited.
 *
 * Portions derived from Grist (Copyright Grist Labs), used under the Apache
 * License, Version 2.0. See the NOTICE file for details.
 */

import { PluginInstance } from "app/common/PluginInstance";
import { InternalImportSourceAPI } from "app/plugin/InternalImportSourceAPI";
import { ImportSource } from "app/plugin/PluginManifest";
import { checkers } from "app/plugin/TypeCheckers";

/**
 * Encapsulate together an import source contribution with its plugin instance and a callable stub
 * for the ImportSourceAPI. Exposes as well a `fromArray` static method to get all the import
 * sources from an array of plugins instances.
 */
export class ImportSourceElement {
  /**
   * Get all import sources from an array of plugin instances.
   */
  public static fromArray(pluginInstances: PluginInstance[]): ImportSourceElement[] {
    const importSources: ImportSourceElement[] = [];
    for (const plugin of pluginInstances) {
      const definitions = plugin.definition.manifest.contributions.importSources;
      if (definitions) {
        for (const importSource of definitions) {
          importSources.push(new ImportSourceElement(plugin, importSource));
        }
      }
    }
    return importSources;
  }

  public importSourceStub: InternalImportSourceAPI;

  private constructor(public plugin: PluginInstance, public importSource: ImportSource) {
    this.importSourceStub = plugin.getStub<InternalImportSourceAPI>(importSource.importSource,
      checkers.InternalImportSourceAPI);
  }
}
